import { type ActorRefFrom, createActor } from "xstate";
import {
  isValidSession,
  type Session,
  SESSION_PAYLOAD_KEY,
  SESSION_STORAGE_KEYS,
  sessionMachine,
  type StoredAccount,
  type TokenStorage,
} from "@aula/shared";
import { readActiveAccountSync } from "./accounts";

/** 24 hours in milliseconds (placeholder expiry; the server hydrates the real one). */
const ONE_DAY_MS = 86_400_000;

/**
 * Storage implementation for Web using localStorage.
 */
const webStorage: TokenStorage = {
  // oxlint-disable-next-line typescript-eslint/require-await
  async getItem(key: string) {
    return globalThis.localStorage.getItem(key);
  },

  // oxlint-disable-next-line typescript-eslint/require-await
  async setItem(key: string, value: string) {
    if (value === "") {
      globalThis.localStorage.removeItem(key);
    } else {
      globalThis.localStorage.setItem(key, value);
    }
  },
};

/**
 * Synchronously removes all session-related keys from localStorage.
 * Use this for "atomic" logouts that survive a page refresh.
 */
export function clearSessionStorage() {
  for (const key of SESSION_STORAGE_KEYS) {
    globalThis.localStorage.removeItem(key);
  }
  console.info("[session] Atomic storage clear completed.");
}

/**
 * Multi-account boot reconciliation (web): the session machine primes from the
 * `aula_session_payload` slot, but the source of truth for "who is active" is
 * the accounts registry (`aula_accounts`). These can drift — e.g. a tab that
 * switched accounts only re-primes the live machine, or a payload from a
 * previous identity lingers. If we let the machine prime from a stale/absent
 * payload it resolves to a DIFFERENT user (or to visitor) than the active
 * account, which flips `activeUserId` after priming and thrashes the per-user
 * PowerSync DB instances.
 *
 * To keep boot deterministic we reconcile synchronously BEFORE the actor
 * starts: when a real account is active, ensure the persisted payload matches
 * that account; when no real account is active, clear any stale payload so the
 * machine primes cleanly into visitor mode.
 */
function reconcilePersistedSessionWithActiveAccount(): void {
  try {
    const active = readActiveAccountSync();
    if (active?.kind !== "real") {
      // No active real account → make sure we don't prime into a ghost session.
      const raw = globalThis.localStorage.getItem(SESSION_PAYLOAD_KEY);
      if (raw) {
        globalThis.localStorage.removeItem(SESSION_PAYLOAD_KEY);
      }
      return;
    }
    const raw = globalThis.localStorage.getItem(SESSION_PAYLOAD_KEY);
    let payloadUserId: string | null = null;
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (isValidSession(parsed)) {
          payloadUserId = parsed.user.id;
        }
      } catch {
        payloadUserId = null;
      }
    }
    if (payloadUserId !== active.userId) {
      globalThis.localStorage.setItem(
        SESSION_PAYLOAD_KEY,
        JSON.stringify(sessionFromAccount(active)),
      );
    }
  } catch (error) {
    console.warn("[session] reconcile error:", error);
  }
}

reconcilePersistedSessionWithActiveAccount();

/**
 * A global singleton actor for the session state.
 * Initialized with web-specific storage.
 */
export const sessionActor: ActorRefFrom<typeof sessionMachine> = createActor(sessionMachine, {
  input: {
    storage: webStorage,
  },
}).start();

/**
 * Builds a minimal {@link Session}-shaped payload for the persisted session
 * slot from a stored account. The token is redacted (matching the machine's
 * own persistence contract); the real Bearer token lives in the accounts
 * registry. The server `get-session` query rehydrates the full profile.
 */
function sessionFromAccount(account: StoredAccount): Session {
  return {
    user: {
      id: account.userId,
      email: account.email,
      name: account.displayName,
      image: account.avatar ?? null,
    },
    session: {
      id: account.userId,
      userId: account.userId,
      expiresAt: new Date(Date.now() + ONE_DAY_MS).toISOString(),
      token: "REDACTED",
    },
  };
}

/**
 * Re-primes the session actor for a freshly-activated account during an
 * in-app account switch. Writes the account's redacted session payload to the
 * persisted slot and pushes it into the machine via `SERVER_REPLY`, so the UI
 * reflects the new identity immediately (without a full reload). A real
 * account gets its session hydrated; a guest gets `LOGOUT` (visitor mode).
 *
 * CRITICAL: this never clears any database — switching must preserve data.
 */
export function primeSessionForAccount(account: StoredAccount | null): void {
  if (!account || account.kind === "guest") {
    globalThis.localStorage.removeItem(SESSION_PAYLOAD_KEY);
    sessionActor.send({ type: "LOGOUT" });
    return;
  }
  const session = sessionFromAccount(account);
  globalThis.localStorage.setItem(SESSION_PAYLOAD_KEY, JSON.stringify(session));
  sessionActor.send({ type: "SERVER_REPLY", session, error: null });
}
